/**
 * Upload IFS document to OneDrive (Download-to-Teams flow).
 * Single thunk drives the flow; loading/error and legacy spinner/success message are coordinated here.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { DATA_LOAD_STATUS } from "../../constants/metaData";
import {
  ON_UPLOAD_ONEDRIVE_STATUS_CHANGED,
  ON_MESSAGE_BOX_INVOKE,
  ON_UPLOAD_ONEDRIVE_FILES,
  ON_IFS_DOCUMENTS_SELECTED,
  ON_SERVER_ERROR_MESSAGE_INVOKE,
} from "../../constants/actionTypes";
import { ERROR_TYPE, SELECTED_DOCUMENTS_UPLOAD_FAILED, DOWNLOADING_DOCUMENT } from "../../constants/messageTypes";
import UserAuth from "../../../auth/userAuth";
import AuthTeams from "../../../auth/AuthTeams";
import { uploadToOneDrive, downloadTemplateToOneDrive } from "../../api/documentsAPI";
import { serializeError } from "../../utils/serializeError";

const UPLOAD_ERROR_MESSAGE = SELECTED_DOCUMENTS_UPLOAD_FAILED;
const userAuth = new UserAuth();

/** Payload: { item, teamId, channelId, tenantId, oneDriveRoot? } */
export const uploadDocumentToOneDrive = createAsyncThunk(
  "documents/uploadToOneDrive/uploadDocumentToOneDrive",
  async (
    { item, teamId, channelId, tenantId, oneDriveRoot },
    { dispatch, rejectWithValue }
  ) => {
    // Pass oneDriveRoot as-is: if caller sends a value (e.g. "ChannelDoc", "MainMenu") use it; otherwise undefined → API sends oneDriveRoot=undefined
    const root =
      oneDriveRoot !== undefined && oneDriveRoot !== "" ? oneDriveRoot : undefined;
    dispatch({
      type: ON_UPLOAD_ONEDRIVE_STATUS_CHANGED,
      payload: DATA_LOAD_STATUS.inProgress,
    });

    try {
      const tokenResponse = await userAuth.getUserToken(true);
      const accessToken = tokenResponse?.accessToken ?? null;
      if (!accessToken) {
        return rejectWithValue({ message: "No access token" });
      }

      const items = [
        {
          class: item.class,
          docNo: item.docNo,
          sheetNo: item.sheetNo,
          docRev: item.docRev,
          docType: item.docType,
          fileNo: item.fileNo,
          title: item.title,
          fileExtension: item.fileExtension,
          // General-app download → AddoShare/Temp on the API (checkout keeps isDownloaded: false → AddoShare root).
          isDownloaded: true,
        },
      ];

      const { status } = await uploadToOneDrive(accessToken, {
        tenantId,
        teamId: teamId ?? "",
        channelId: channelId ?? "",
        oneDriveRoot: root,
        items,
      });

      dispatch({
        type: ON_UPLOAD_ONEDRIVE_STATUS_CHANGED,
        payload: DATA_LOAD_STATUS.completed,
      });
      dispatch({ type: ON_UPLOAD_ONEDRIVE_FILES, payload: [] });
      dispatch({ type: ON_IFS_DOCUMENTS_SELECTED, payload: [] });

      if (status === 200) {
        const displayTitle = decodeURIComponent(item?.title || "");
        //const successMessage = `${displayTitle} was successfully downloaded`;
        const successMessage = "Document downloaded successfully!";
        return { success: true, message: successMessage };
      }

      dispatch({
        type: ON_MESSAGE_BOX_INVOKE,
        payload: {
          type: ERROR_TYPE,
          message: SELECTED_DOCUMENTS_UPLOAD_FAILED,
          errorMessage: "",
          error: null,
        },
      });
      return rejectWithValue(new Error(`Upload status ${status}`));
    } catch (err) {
      dispatch({
        type: ON_UPLOAD_ONEDRIVE_STATUS_CHANGED,
        payload: DATA_LOAD_STATUS.error,
      });
      dispatch({ type: ON_IFS_DOCUMENTS_SELECTED, payload: [] });
      const payload = serializeError(err);
      const statusCode = err?.response?.status;
      dispatch({
        type: ON_SERVER_ERROR_MESSAGE_INVOKE,
        payload: {
          response: err?.response,
          statusCode: statusCode ?? 0,
          error: payload ?? err,
        },
      });
      return rejectWithValue(payload ?? err);
    }
  }
);

export const uploadTemplateToOneDrive = createAsyncThunk(
  "documents/uploadToOneDrive/uploadTemplateToOneDrive",
  async (
    { template, teamId, channelId, tenantId, oneDriveRoot },
    { dispatch, rejectWithValue }
  ) => {
    dispatch({
      type: ON_UPLOAD_ONEDRIVE_STATUS_CHANGED,
      payload: DATA_LOAD_STATUS.inProgress,
    });

    try {
      const tokenResponse = await userAuth.getUserToken(true);
      const accessToken = tokenResponse?.accessToken ?? null;
      if (!accessToken) {
        return rejectWithValue({ message: "No access token" });
      }

      const auth = new AuthTeams();
      const userId = auth.getUserId();
      const webUser = auth.getEmail();

      const { status } = await downloadTemplateToOneDrive(accessToken, {
        tenantId,
        teamId: teamId ?? "",
        channelId: channelId ?? "",
        oneDriveRoot,
        userId,
        webUser,
        docClass: template.DocClass,
        docNo: template.DocNo,
        docSheet: template.DocSheet,
        docRev: template.DocRev,
        formatSize: template.FormatSize,
        languageCode: template.LanguageCode,
        title: template.Title,
      });

      dispatch({
        type: ON_UPLOAD_ONEDRIVE_STATUS_CHANGED,
        payload: DATA_LOAD_STATUS.completed,
      });

      if (status === 200) {
        return { success: true, message: "Document downloaded successfully!" };
      }

      dispatch({
        type: ON_MESSAGE_BOX_INVOKE,
        payload: {
          type: ERROR_TYPE,
          message: SELECTED_DOCUMENTS_UPLOAD_FAILED,
          errorMessage: "",
          error: null,
        },
      });
      return rejectWithValue(new Error(`Upload status ${status}`));
    } catch (err) {
      dispatch({
        type: ON_UPLOAD_ONEDRIVE_STATUS_CHANGED,
        payload: DATA_LOAD_STATUS.error,
      });
      const payload = serializeError(err);
      const statusCode = err?.response?.status;
      dispatch({
        type: ON_SERVER_ERROR_MESSAGE_INVOKE,
        payload: {
          response: err?.response,
          statusCode: statusCode ?? 0,
          error: payload ?? err,
        },
      });
      return rejectWithValue(payload ?? err);
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  /** Single message for dialog: set by slice for pending / fulfilled / rejected. */
  message: "",
  /** True when result dialog is open (success or error); close with closeDownloadSuccessDialog. */
  downloadSuccessDialogOpen: false,
};

const uploadToOneDriveSlice = createSlice({
  name: "documents/uploadToOneDrive",
  initialState,
  reducers: {
    clearUploadToOneDriveError: (state) => {
      state.error = null;
    },
    closeDownloadSuccessDialog: (state) => {
      state.message = "";
      state.downloadSuccessDialogOpen = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadDocumentToOneDrive.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = DOWNLOADING_DOCUMENT;
        state.downloadSuccessDialogOpen = false;
      })
      .addCase(uploadDocumentToOneDrive.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.message = action.payload?.message ?? "";
        state.downloadSuccessDialogOpen = true;
      })
      .addCase(uploadDocumentToOneDrive.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? serializeError(action.error);
        state.message = typeof action.payload?.message === "string" ? action.payload.message : UPLOAD_ERROR_MESSAGE;
        state.downloadSuccessDialogOpen = true;
      })
      .addCase(uploadTemplateToOneDrive.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = DOWNLOADING_DOCUMENT;
        state.downloadSuccessDialogOpen = false;
      })
      .addCase(uploadTemplateToOneDrive.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.message = action.payload?.message ?? "";
        state.downloadSuccessDialogOpen = false;
      })
      .addCase(uploadTemplateToOneDrive.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? serializeError(action.error);
        state.message = typeof action.payload?.message === "string" ? action.payload.message : UPLOAD_ERROR_MESSAGE;
        state.downloadSuccessDialogOpen = false;
      });
  },
});

export const { clearUploadToOneDriveError, closeDownloadSuccessDialog } = uploadToOneDriveSlice.actions;
export default uploadToOneDriveSlice.reducer;
